import { Component, Input, OnInit, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Base } from '../../services/base';
import { Field } from '../../models/field';
import { environment } from '../../../environments/environment';
import { AnalysisManagementService } from '../../services/analysis-management.service';
import { FieldImageryLegendDto } from '../../dto/FieldImageryLegendDto';
import { FieldLegend } from '../../models/fieldLegend';
import { MapState, MapSyncService } from '../map-sequence-services/map-sync.service';
import { Subscription } from 'rxjs';

declare var google: any;

@Component({
  selector: 'app-sequence-card',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule, MatProgressSpinnerModule],
  templateUrl: './sequence-card.component.html',
  styleUrl: './sequence-card.component.css',
})
export class SequenceCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() card: any;
  @ViewChild('map') map: GoogleMap;

  mapId: string;
  zoom = 15;
  center: google.maps.LatLng;
  bounds: google.maps.LatLngBounds;
  options: google.maps.MapOptions = {
    mapTypeId: 'hybrid',
    disableDefaultUI: true,
    zoomControl: false,
    scrollwheel: false,
    disableDoubleClickZoom: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    isFractionalZoomEnabled: true
  };
  polygon: any[] = [];
  polygonOptions = {
    strokeColor: environment.google.mapConfig.polygonStrokeColour,
    strokeWeight: 4,
    fillOpacity: 0.0
  };
  overlayImage: string = '';
  fieldLegends: FieldLegend[] = [];
  isLoadingLegends: boolean = false;
  private mapSyncSubscription: Subscription;
  private zoomSubscription: Subscription;
  private resetSubscription: Subscription;
  private legendSubscription: Subscription;
  showLegends: boolean = false;
  private isUpdatingFromSync = false;
  private isZoomingFromCommand = false;
  private intersectionObserver: IntersectionObserver;
  private pendingMapState: MapState | null = null;
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;
  private onBlur: () => void;
  private onWheel: (e: WheelEvent) => void;

  constructor(
    private base: Base,
    private analysisManagementService: AnalysisManagementService,
    private mapSyncService: MapSyncService
  ) { }

  ngOnInit(): void {
    try {
      this.mapId = `map-${this.card?.analysisId || Math.random()}-${this.card?.propertyTypeId || Math.random()}`;
      
      if (this.card?.fieldId && this.card?.mapUrl) {
        this.overlayImage = this.card.mapUrl;
        this.loadFieldBoundary(this.card.fieldId);
        if (this.card?.analysisId && this.card?.propertyTypeId) {
          this.loadLegend(this.card.analysisId, this.card.propertyTypeId);
        }
      }
      
      // Subscribe to map state changes
      this.mapSyncSubscription = this.mapSyncService.mapState$.subscribe(state => {
        if (!this.isUpdatingFromSync && state) {
          if (this.map?.googleMap) {
            this.applyMapState(state);
          } else {
            this.pendingMapState = state;
          }
        }
      });

      // Subscribe to zoom commands from parent
      this.zoomSubscription = this.mapSyncService.zoomCommand$.subscribe(delta => {
        if (this.map?.googleMap) {
          this.isZoomingFromCommand = true;
          const current = Math.round((this.map.googleMap.getZoom() || this.zoom) * 2) / 2;
          this.map.googleMap.setZoom(Math.max(1, Math.min(22, current + delta)));
          setTimeout(() => this.isZoomingFromCommand = false, 300);
        }
      });

      // Subscribe to reset commands from parent
      this.resetSubscription = this.mapSyncService.reset$.subscribe(() => {
        if (this.map?.googleMap && this.bounds) {
          this.map.fitBounds(this.bounds);
        }
      });

      // Subscribe to legend toggle
      this.legendSubscription = this.mapSyncService.showLegends$.subscribe(show => {
        this.showLegends = show;
      });
    } catch (error) {
      console.error('Error initializing sequence card:', error);
    }
  }

  ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        if (this.bounds && this.map?.googleMap) {
          this.map.fitBounds(this.bounds);
          
          this.mapSyncService.registerMap(this.mapId, this.map);
          
          // Apply any pending state from sync
          const latestState = this.mapSyncService.getLatestState();
          if (latestState) {
            this.applyMapState(latestState);
          }
          
          // Setup Ctrl+Scroll zoom
          this.setupCustomScrollZoom();
        }
      }, 100);
      
      // Setup intersection observer for out-of-view maps
      this.setupIntersectionObserver();
    } catch (error) {
      console.error('Error fitting map bounds:', error);
    }
  }

  loadFieldBoundary(id: number): void {
    try {
      // The maps module hands the card its field's own boundary. Without one, fall
      // back to what the old field and mapping views leave in sessionStorage and
      // Base, which is only right when the card was opened from those views.
      if (Array.isArray(this.card?.boundary)) {
        this.loadCardBoundary(this.card.boundary);
        return;
      }

      const fieldsData = sessionStorage.getItem('fields');
      if (!fieldsData) {
        console.warn('No fields data in sessionStorage');
        return;
      }

      const fields: Field[] = JSON.parse(fieldsData);
      const currentField = fields.find(field => field.id == id);

      if (!currentField) {
        console.warn(`Field with id ${id} not found`);
        return;
      }

      // Use analysis lat/lng if available, otherwise use field lat/lng
      const lat1 = this.card?.latMin ?? currentField.lat1;
      const lng1 = this.card?.lngMin ?? currentField.lng1;
      const lat2 = this.card?.latMax ?? currentField.lat2;
      const lng2 = this.card?.lngMax ?? currentField.lng2;

      if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) {
        console.warn('Invalid coordinates for field boundary');
        return;
      }

      this.bounds = new google.maps.LatLngBounds();
      this.bounds.extend(new google.maps.LatLng(lat1, lng1));
      this.bounds.extend(new google.maps.LatLng(lat2, lng2));
      this.center = new google.maps.LatLng(lat1, lng1);

      this.base.fetchAnalysis().subscribe({
        next: (analysis) => {
          if (analysis?.path && Array.isArray(analysis.path) && analysis.path.length > 0) {
            this.polygon = analysis.path;

            if (this.map?.googleMap && this.bounds) {
              setTimeout(() => {
                this.map.fitBounds(this.bounds);
              }, 100);
            }
          }
        },
        error: (error) => {
          console.error('Error fetching analysis data:', error);
        }
      });
    } catch (error) {
      console.error('Error loading field boundary:', error);
    }
  }

  /**
   * Bounds and outline from the card alone.
   *
   * The imagery's own extent places the map; the boundary's extent stands in when
   * a row comes back without one. An empty boundary (a refresh straight onto the
   * sequence URL, before the maps page has loaded the farm) leaves the outline off
   * rather than drawing another field's.
   */
  private loadCardBoundary(rings: google.maps.LatLngLiteral[][]): void {
    const points = rings.reduce((all, ring) => all.concat(ring), [] as google.maps.LatLngLiteral[]);
    const { latMin, lngMin, latMax, lngMax } = this.card;
    const hasImageryBounds = latMin != null && lngMin != null && latMax != null && lngMax != null;

    if (!hasImageryBounds && points.length === 0) {
      console.warn('No imagery bounds or boundary for sequence card');
      return;
    }

    this.bounds = new google.maps.LatLngBounds();
    if (hasImageryBounds) {
      this.bounds.extend(new google.maps.LatLng(latMin, lngMin));
      this.bounds.extend(new google.maps.LatLng(latMax, lngMax));
    } else {
      points.forEach(p => this.bounds.extend(new google.maps.LatLng(p.lat, p.lng)));
    }
    this.center = this.bounds.getCenter();
    this.polygon = points.length > 0 ? [rings] : [];
  }

  loadLegend(analysisId: number, propertyTypeId: number): void {
    try {
      this.isLoadingLegends = true;
      const legendDto: FieldImageryLegendDto = {
        analysisId: analysisId,
        propertyTypeId: propertyTypeId
      };

      this.analysisManagementService.queueFieldImageryLegend(legendDto).subscribe({
        next: (result) => {
          if (result && Array.isArray(result)) {
            this.fieldLegends = result;
          }
          this.isLoadingLegends = false;
        },
        error: (error) => {
          console.error('Error loading legend:', error);
          this.isLoadingLegends = false;
        }
      });
    } catch (error) {
      console.error('Error in loadLegend:', error);
      this.isLoadingLegends = false;
    }
  }

  onMapChange(): void {
    if (!this.isUpdatingFromSync && !this.isZoomingFromCommand && this.map?.googleMap) {
      const center = this.map.googleMap.getCenter();
      const zoom = this.map.googleMap.getZoom();
      const bounds = this.map.googleMap.getBounds();
      if (center && zoom && bounds) {
        this.mapSyncService.updateMapState({ 
          center, 
          zoom, 
          bounds,
          sourceMapId: this.mapId 
        });
      }
    }
  }
  
private applyMapState(state: MapState): void {
  if (!this.map?.googleMap || state.sourceMapId === this.mapId) return;
  this.isUpdatingFromSync = true;
  this.map.googleMap.setCenter(state.center);
  this.map.googleMap.setZoom(state.zoom);
  setTimeout(() => this.isUpdatingFromSync = false, 50);
}


  private setupCustomScrollZoom(): void {
    if (!this.map?.googleMap) return;

    const mapDiv = this.map.googleMap.getDiv();

    // Block browser's native Ctrl+scroll page zoom when cursor is over the map
    this.onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };
    mapDiv.addEventListener('wheel', this.onWheel, { passive: false });

    // Toggle Google Maps' own scrollwheel zoom based on Ctrl key state
    this.onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' && this.map?.googleMap) {
        this.map.googleMap.setOptions({ scrollwheel: true });
      }
    };

    this.onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' && this.map?.googleMap) {
        this.map.googleMap.setOptions({ scrollwheel: false });
      }
    };

    this.onBlur = () => {
      if (this.map?.googleMap) {
        this.map.googleMap.setOptions({ scrollwheel: false });
      }
    };

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  private setupIntersectionObserver(): void {
    if (!this.map?.googleMap) return;
    
    const mapDiv = this.map.googleMap.getDiv();
    
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.pendingMapState) {
          // Map is now visible, apply pending state
          this.applyMapState(this.pendingMapState);
          this.pendingMapState = null;
        }
      });
    }, { threshold: 0.1 }); // Trigger when 10% visible
    
    this.intersectionObserver.observe(mapDiv);
  }

  ngOnDestroy(): void {
    this.mapSyncService.unregisterMap(this.mapId);
    this.mapSyncSubscription?.unsubscribe();
    this.zoomSubscription?.unsubscribe();
    this.resetSubscription?.unsubscribe();
    this.legendSubscription?.unsubscribe();
    this.intersectionObserver?.disconnect();
    if (this.onKeyDown) window.removeEventListener('keydown', this.onKeyDown);
    if (this.onKeyUp) window.removeEventListener('keyup', this.onKeyUp);
    if (this.onBlur) window.removeEventListener('blur', this.onBlur);
    if (this.onWheel) this.map?.googleMap?.getDiv()?.removeEventListener('wheel', this.onWheel);
  }
}
